import type { OrderByDescriptor } from '../types/common/OrderByDescriptor';
import type { ListingQueryModel } from '../types/listings/ListingQueryModel';
import type { ListingViewModel } from '../types/listings/ListingViewModel';
import type { SellerListingCardViewModel } from '../types/seller/SellerListingCardViewModel';
import type { AdListing } from 'types/adListing';
import { SortingOptions } from 'types/sortingOptions';
import { MediaService } from '../services/MediaService';

/** Sort option string as used in UI (newest | oldest | priceAsc | priceDesc). */
export type ListingSortOption = 'newest' | 'oldest' | 'priceAsc' | 'priceDesc';

/**
 * Map UI sort option to backend OrderBy. Filtering is disabled in list pages; only orderBy is sent.
 */
export function sortOptionToOrderBy(sortOption: ListingSortOption): OrderByDescriptor[] {
  switch (sortOption) {
    case 'priceAsc':
      return [{ field: 'Price', descending: false }];
    case 'priceDesc':
      return [{ field: 'Price', descending: true }];
    case 'oldest':
      return [{ field: 'CreatedAt', descending: false }];
    case 'newest':
    default:
      return [{ field: 'CreatedAt', descending: true }];
  }
}

/** İlanlarım: date sorts use API OrderBy; price sorts stay client-side on the loaded list. */
export function myListingsQueryForSort(sortOption: SortingOptions): Pick<ListingQueryModel, 'OrderBy'> | undefined {
  if (sortOption === SortingOptions.PRICE_ASC || sortOption === SortingOptions.PRICE_DESC) {
    return undefined;
  }
  return { OrderBy: sortOptionToOrderBy(sortOption as ListingSortOption) };
}

/**
 * Map ListingViewModel to AdListing for list/detail UI.
 */
export function listingViewModelToAdListing(lv: ListingViewModel): AdListing {
  const konum = [lv.city?.name, lv.district?.name].filter(Boolean).join(', ') || '';
  const durum = lv.condition?.name === 'Sıfır' ? 'Sıfır' : 'İkinci El';
  const specs = lv.specs ?? {};
  return {
    id: lv.id,
    ownerUserId: lv.userId,
    baslik: lv.title ?? '',
    aciklama: lv.description ?? '',
    fiyat: lv.price,
    kategori: lv.productType?.name ?? '',
    konum,
    tarih: '',
    saticiId: 0,
    saticiAdi: '',
    resimUrl: MediaService.toImageSrc(
      lv.imageUrls?.[0] ?? lv.images?.[0]?.url ?? lv.images?.[0]?.id ?? ''
    ),
    durum,
    province: lv.city?.name ?? undefined,
    district: lv.district?.name ?? undefined,
    islemci: specs['İşlemci'] ?? specs['islemci'],
    ekranKarti: specs['Ekran Kartı'] ?? specs['ekranKarti'],
    ram: specs['RAM'] ?? specs['ram'],
    ssd: specs['SSD'] ?? specs['ssd'],
    hdd: specs['HDD'] ?? specs['hdd'],
    isUserFavorite: lv.isUserFavorite === true,
    rejectionReason: lv.rejectionReason ?? undefined,
    isTested: lv.isTested === true,
  };
}

/**
 * Map seller profile listing card to AdListing for AdListGridItem / AdListImage.
 */
export function sellerListingCardToAdListing(s: SellerListingCardViewModel): AdListing {
  const konum = [s.district, s.city].filter((p): p is string => !!p && p.trim() !== '').join(', ') || '';
  return {
    id: s.listingId,
    baslik: s.title,
    aciklama: '',
    fiyat: s.price,
    kategori: '',
    konum,
    tarih: s.createdAt,
    saticiId: 0,
    saticiAdi: '',
    resimUrl: s.primaryImageUrl
      ? MediaService.toImageSrc(s.primaryImageUrl)
      : '',
    durum: 'İkinci El',
    province: s.city ?? undefined,
    district: s.district ?? undefined,
  };
}
